import { isDefined, isEmpty } from "@flaminem/utils";
import { ElementDefinition } from "cytoscape";

import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { mapIcons } from "src/dynamic-view-renderer/Widgets/RelatedCases/Graph/svgs.ts";
import { RelatedCasesDisplayElementConfig } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { useT } from "src/internationalization";
import { Case } from "src/types/case";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import {
  createSafeNodeData,
  filterGraphElements,
  getCaseId,
  getElementId,
  getIdentifierId,
} from "../graph.utils";

type GetColaMappingInput = {
  elementsConfig: RelatedCasesDisplayElementConfig[];
  caseInfo: {
    caseId?: string;
    alertId: string;
    propertyValues: PropertyValue[];
    elements: Element[];
  };
  relatedCases?: Case[];
  options?: {
    filteredIdentifiers: string[];
    mergeRelatedCases?: boolean;
    isAlert?: boolean;
  };
};

type RelatedElementsCase = {
  relatedCase: Case | null;
  relatedElement: Element | null;
  serverId?: string;
} | null;

export default function useColaMapping({
  elementsConfig,
  caseInfo,
  options,
  relatedCases = [],
}: GetColaMappingInput): ElementDefinition[] {
  const t = useT();
  const { graph } = useDynamicViewSchema();

  const currentCaseId = getCaseId({
    caseId: caseInfo.caseId,
    propertyValues: caseInfo.propertyValues,
  });

  const filteredElements = filterGraphElements(elementsConfig, caseInfo);

  const textOrEmpty = (text: string | undefined) => text?.trim() || t("general.empty");

  const alertElementsGraphData = elementsConfig.flatMap((elemConf) => {
    const currentCaseElement = filteredElements.find(
      (x) => x.configurableType.code === elemConf.code
    );

    if (!currentCaseElement) {
      return null;
    }

    const currentElementId = getElementId(currentCaseElement);

    const elementIcon = graph.getIcon(currentCaseElement.schemaElement.code, "element");

    return [
      {
        data: createSafeNodeData({
          id: currentElementId,
          label: `${currentCaseElement.schemaElement.name}`,
          type: "element",
          code: currentCaseElement.schemaElement.code,
          icon: mapIcons(elementIcon),
          group: elemConf.code,
          serverId: caseInfo.caseId,
        }),
      },
      {
        data: createSafeNodeData({
          id: `${currentCaseId}-${currentElementId}`,
          label: ``,
          type: "edge-case",
          source: currentCaseId,
          target: currentElementId,
          group: elemConf.code,
          serverId: caseInfo.caseId,
        }),
      },
    ];
  });

  const identifiersGraphData =
    filteredElements?.flatMap((element) => {
      const elementCode = element.schemaElement.code;

      return element.identifiers
        .filter(
          (identifier) =>
            isEmpty(options?.filteredIdentifiers) ||
            options?.filteredIdentifiers.includes(identifier.id)
        )
        .flatMap((identifier) => {
          const identifierId = getIdentifierId(identifier.value, identifier.schemaIdentifier.code);

          // TODO: filter relatedElements based on the configuration
          const relatedElements = relatedCases
            .flatMap((relatedCase) =>
              relatedCase.elements.map((element) => ({
                ...element,
                caseId: relatedCase.id,
              }))
            )
            .filter((element) =>
              // filter out elements which are not in the configuration
              elementsConfig.some((elem) => elem.code === element.schemaElement.code)
            )
            // select only elements which have an identifier matching the current identifierId
            .filter((element) =>
              element.identifiers.some(
                (identifier) =>
                  getIdentifierId(identifier.value, identifier.schemaIdentifier.code) ===
                  identifierId
              )
            );

          const relatedElementsData = relatedElements.flatMap((relatedElement) => {
            const elementIcon = graph.getIcon(relatedElement.schemaElement.code, "element");

            return [
              {
                data: createSafeNodeData({
                  id: getElementId(relatedElement),
                  label: `${relatedElement.schemaElement.name}`,
                  type: "element",
                  code: relatedElement.schemaElement.code,
                  group: elementCode,
                  icon: mapIcons(elementIcon),
                  serverId: relatedElement.caseId,
                }),
              },
              {
                data: createSafeNodeData({
                  id: `${identifierId}-${getElementId(relatedElement)}`,
                  label: ``,
                  type: "edge-elem",
                  element: elementCode,
                  source: identifierId,
                  target: getElementId(relatedElement),
                  group: elementCode,
                  serverId: relatedElement.caseId,
                }),
              },
            ];
          });

          // If there's a related element, create the case node and edge connecting to the element
          const relatedElementsCases: RelatedElementsCase[] = relatedElements.flatMap(
            (relatedElement) => {
              const relatedCase = relatedCases.find((relatedCase) =>
                relatedCase.elements.some((element) => element.id === relatedElement.id)
              );

              if (!relatedCase) {
                return { relatedCase: null, relatedElement: null };
              }

              const matchingElement = relatedCase.elements.find(
                (element) => element.id === relatedElement.id
              );

              return {
                relatedCase: { ...relatedCase, id: `${relatedCase.id}-${relatedElement.id}` },
                relatedElement: matchingElement ?? null,
                serverId: relatedCase.id,
              };
            }
          );

          const relatedElementsCasesData = relatedElementsCases
            .filter(isDefined)
            .flatMap((relatedCase) => {
              if (!relatedCase.relatedCase || !relatedCase.relatedElement) {
                return [];
              }

              const commonRelatedCaseId = getCaseId({
                caseId: relatedCase.relatedCase?.id,
                propertyValues: relatedCase.relatedCase?.properties,
              });

              const relatedCaseId = options?.mergeRelatedCases
                ? commonRelatedCaseId
                : `${relatedCase.relatedCase.id}`;
              const elementIcon = graph.getIcon("case", "element");

              return [
                {
                  data: createSafeNodeData({
                    id: relatedCaseId,
                    label: `${t("widget.relatedCases.graph.case")}: ${commonRelatedCaseId}`,
                    type: "case",
                    icon: mapIcons(elementIcon),
                    group: elementCode,
                    serverId: relatedCase.serverId,
                  }),
                },
                {
                  data: createSafeNodeData({
                    id: `${relatedCaseId}-${getElementId(relatedCase.relatedElement)}`,
                    label: ``,
                    type: "edge-case",
                    element: elementCode,
                    source: getElementId(relatedCase.relatedElement),
                    target: relatedCaseId,
                    group: elementCode,
                    serverId: relatedCase.serverId,
                  }),
                },
              ];
            });

          const identifierIcon = graph.getIcon(identifier.identifierType?.code, "identifier");

          console.assert(
            !!identifierIcon,
            "Identifier icon is required",
            identifier.identifierType?.code,
            graph.mappers.icons.identifiers
          );

          return [
            {
              data: createSafeNodeData({
                id: identifierId,
                label: `${identifier.schemaIdentifier?.name}: ${textOrEmpty(identifier.value)}`,
                type: "identifier",
                element: elementCode,
                group: elementCode,
                identifierCode: identifier.schemaIdentifier?.name,
                icon: mapIcons(identifierIcon),
                serverId: caseInfo.caseId,
              }),
            },
            {
              data: createSafeNodeData({
                id: `${identifierId}-${getElementId(element)}`,
                label: ``,
                type: "edge-elem",
                element: elementCode,
                target: identifierId,
                source: getElementId(element),
                group: elementCode,
                serverId: caseInfo.caseId,
              }),
            },
            ...relatedElementsData,
            ...relatedElementsCasesData,
          ];
        });
    }) ?? [];

  const elementIcon = graph.getIcon("case-alert", "element");

  return [
    {
      data: createSafeNodeData({
        id: currentCaseId,
        label: `Alerte: ${currentCaseId}`,
        type: "case-alert",
        group: "alert",
        icon: mapIcons(elementIcon),
        serverId: caseInfo.alertId,
      }),
    },
    ...alertElementsGraphData.filter(isDefined),
    ...identifiersGraphData.filter(isDefined),
  ];
}
